// ============================================================
// api.js — thin wrapper around Babb Finance v2 Worker
// ============================================================
const API_BASE = 'https://babb-finance-api.bryan-babb1.workers.dev';

async function request(path, opts = {}) {
  const r = await fetch(API_BASE + path, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
  });
  // Try to parse JSON regardless of status, since Worker always returns JSON
  let data;
  try { data = await r.json(); } catch { data = null; }
  if (!r.ok) {
    const msg = data?.error ?? `HTTP ${r.status}`;
    const e = new Error(msg);
    e.status = r.status;
    e.body = data;
    throw e;
  }
  return data;
}

const get   = (p)         => request(p);
const post  = (p, body)   => request(p, { method: 'POST', body: JSON.stringify(body) });
const patch = (p, body)   => request(p, { method: 'PATCH', body: JSON.stringify(body) });
const del   = (p)         => request(p, { method: 'DELETE' });

export const api = {
  bootstrap: () => get('/bootstrap'),

  // transactions
  listTransactions: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return get(`/transactions${qs ? '?' + qs : ''}`);
  },
  createTransaction: (t) => post('/transactions', t),
  updateTransaction: (id, t) => patch(`/transactions/${id}`, t),
  deleteTransaction: (id) => del(`/transactions/${id}`),

  // recurring
  listRecurring: () => get('/recurring'),
  createRecurring: (r) => post('/recurring', r),
  updateRecurring: (id, r) => patch(`/recurring/${id}`, r),
  deleteRecurring: (id) => del(`/recurring/${id}`),

  // forecast
  getForecast: (from, to) => get(`/forecast?from=${from}&to=${to}`),
  payForecast:    (recurring_id, scheduled_on, actual_cents = null) =>
    post('/forecast/pay', { recurring_id, scheduled_on, actual_cents }),
  skipForecast:   (recurring_id, scheduled_on) =>
    post('/forecast/skip', { recurring_id, scheduled_on }),
  unmarkForecast: (recurring_id, scheduled_on) =>
    post('/forecast/unmark', { recurring_id, scheduled_on }),
  rescheduleForecast: (recurring_id, scheduled_on, actual_on) =>
    post('/forecast/reschedule', { recurring_id, scheduled_on, actual_on }),
  setForecastAmount: (recurring_id, scheduled_on, actual_cents) =>
    post('/forecast/setAmount', { recurring_id, scheduled_on, actual_cents }),

  // simple CRUD
  listCategories: () => get('/categories'),
  createCategory: (c) => post('/categories', c),
  updateCategory: (id, c) => patch(`/categories/${id}`, c),
  deleteCategory: (id) => del(`/categories/${id}`),

  listAccounts:   () => get('/accounts'),
  createAccount:  (a) => post('/accounts', a),
  updateAccount:  (id, a) => patch(`/accounts/${id}`, a),
  deleteAccount:  (id) => del(`/accounts/${id}`),

  listDebts:      () => get('/debts'),
  createDebt:     (d) => post('/debts', d),
  updateDebt:     (id, d) => patch(`/debts/${id}`, d),
  deleteDebt:     (id) => del(`/debts/${id}`),

  listSinkingFunds:   () => get('/sinking_funds'),
  createSinkingFund:  (s) => post('/sinking_funds', s),
  updateSinkingFund:  (id, s) => patch(`/sinking_funds/${id}`, s),
  deleteSinkingFund:  (id) => del(`/sinking_funds/${id}`),

  listGoals:      () => get('/goals'),
  createGoal:     (g) => post('/goals', g),
  updateGoal:     (id, g) => patch(`/goals/${id}`, g),
  deleteGoal:     (id) => del(`/goals/${id}`),

  listBudgets:    () => get('/budgets'),
  createBudget:   (b) => post('/budgets', b),
  updateBudget:   (id, b) => patch(`/budgets/${id}`, b),
  deleteBudget:   (id) => del(`/budgets/${id}`),

  // settings
  listSettings:   () => get('/settings'),
  setSetting:     (key, value) => post('/settings', { key, value }),
};

export { API_BASE };
